import { useEffect, useMemo, useState, type RefObject } from "react";
import { localDateAt, type ScheduleResponse } from "@edu/shared";
import {
  addressWithoutLink,
  findEntryOfLesson,
  parseLessonLink,
  weekCovers,
  P_AT,
  P_LESSON,
} from "./lesson-link.ts";

/**
 * Занятие, названное адресом: показать его неделю и раскрыть его карточку.
 *
 * Отдельным хуком, а не куском экрана, по двум причинам. У раскрытия есть своё
 * состояние («какого ответа ждём», «нашли, но колонки не видно»), и внутри
 * пятисотстрочного экрана оно читалось бы как ещё три переменные среди прочих.
 * И решения его чистые (`lesson-link.ts`), то есть проверяемые тестами, а
 * компонентных тестов в проекте нет и не планируется (раздел 9 CLAUDE.md).
 *
 * Четыре правила, и каждое закрывает поломку, найденную ревью 03.09.2026.
 *
 * 1. **Адрес чистится ПОСЛЕ работы, а не до неё.** Указатель в памяти вкладки
 *    ссылку не заменяет: `getSchedule` на канале 128 кбит/с отваливается по
 *    таймауту, экран показывает красную строку, и кнопки «повторить» на нём
 *    нет. Перезагрузка страницы это единственное, что человеку остаётся, и
 *    вычеркнутая заранее ссылка не пережила бы её никогда
 * 2. **Ждём ответ, покрывающий неделю занятия**, а не любой (`weekCovers` по
 *    границам самого ответа). Иначе раскрытие сгорает на чужой неделе: в
 *    открытой вкладке в памяти лежит прошлый ответ, а на медленном канале
 *    человек успевает пролистать неделю, пока запрос в полёте
 * 3. **День берётся у НАЙДЕННОГО занятия, а не только из ссылки.** Строка ищется
 *    по всей неделе, а коробка живёт только в видимых колонках, и на телефоне
 *    колонка одна. Занятие во вторник у смотрящего в четверг находилось и не
 *    раскрывалось, молча. Найдя строку, двигаем якорь на её день и раскрываем
 *    на следующем коммите
 * 4. **Листание отменяет ссылку** (`addressWithoutLink` в `go` у экрана):
 *    человек взял окно в свои руки, и прыгать на занятие после этого значит
 *    отнимать у него управление
 */
export function useAddressedEntry(args: {
  params: URLSearchParams;
  setParams: (next: URLSearchParams, options: { replace: boolean }) => void;
  /** Зона СМОТРЯЩЕГО: и день ссылки, и день найденного занятия считаются в ней. */
  zone: string;
  /** Последний ответ расписания. Его границы и говорят, та ли это неделя. */
  data: ScheduleResponse | null;
  /** Сетка в DOM: в ней ищется коробка занятия, от неё карточка считает место. */
  grid: RefObject<HTMLElement | null>;
  /** Раскрыть карточку. Ссылка обязана быть стабильной: иначе эффект гоняется зря. */
  onOpen: (key: string, anchor: HTMLElement) => void;
}): string | null {
  const { params, setParams, zone, data, grid, onOpen } = args;

  const link = useMemo(
    () => parseLessonLink(params.get(P_LESSON), params.get(P_AT), zone),
    [params, zone],
  );
  /**
   * День, на который якорь пришлось подвинуть ради найденного занятия.
   *
   * ⚠️ Помнится ВМЕСТЕ с уроком, ради которого подвинут, а не одной датой.
   * Вкладка живёт долго, и второй адрес с занятием прилетает в тот же
   * смонтированный экран; одинокая дата от прошлой ссылки увела бы новую на
   * чужую неделю, причём молча. Дальше того же занятия день не нужен вовсе: он
   * уже лежит в `?day=`, и вторая копия спорила бы с листанием.
   */
  const [nudge, setNudge] = useState<{ lessonId: string; day: string } | null>(null);
  const nudged = link !== null && nudge?.lessonId === link.lessonId ? nudge.day : null;

  useEffect(() => {
    if (link === null) return;
    // Ответа нет или приехал не тот: ссылка остаётся в адресе, ждём дальше
    if (data === null || !weekCovers(data.from, data.to, link.day)) return;

    const entry = findEntryOfLesson(data.entries, link.lessonId, link.at);
    /**
     * Не нашлось это штатный случай (урок без времени, осиротевший, чужой,
     * удалённый, занятие уехало со своей недели), и ответ на него не красная
     * строка, а обычное расписание.
     *
     * ⚠️ Остаёмся на неделе ССЫЛКИ, а не уходим на текущую. Неделя выведена из
     * момента самого урока, человек только что был на этом уроке, и его неделя
     * это лучший ответ на вопрос «где искать», который у нас есть. Прыжок на
     * сегодня выглядел бы так, будто ссылку просто не прочитали, и стёр бы
     * единственную подсказку о том, где занятие стояло.
     */
    if (entry === null) {
      setNudge(null);
      setParams(addressWithoutLink(params, link.day), { replace: true });
      return;
    }

    const day = localDateAt(entry.at, zone);
    const box = boxOfEntry(grid.current, entry.key);
    if (box === null) {
      // Колонки этого дня на экране нет: двигаем якорь и раскрываем на
      // следующем коммите. Ровно одна попытка: если коробки нет и после
      // сдвига, оставляем человека на дне занятия без карточки, а не крутим
      // эффект по кругу
      if (nudged !== day) {
        setNudge({ lessonId: link.lessonId, day });
        return;
      }
      setParams(addressWithoutLink(params, day), { replace: true });
      return;
    }

    // Занятие может стоять выше или ниже видимого куска календаря: карточка
    // считает своё место от коробки, и у коробки за краем экрана она прижалась
    // бы к кромке, оторвавшись от занятия, ради связи с которым она заведена
    box.scrollIntoView({ block: "center", inline: "nearest" });
    onOpen(entry.key, box);
    setNudge(null);
    setParams(addressWithoutLink(params, day), { replace: true });
  }, [data, grid, link, nudged, onOpen, params, setParams, zone]);

  /**
   * День, который экран обязан показать.
   *
   * ⚠️ Читается только пока ссылка жива: сдвиг якоря это её рабочее состояние,
   * и переживи он уборку адреса, он спорил бы с листанием недели.
   */
  return link === null ? null : (nudged ?? link.day);
}

/**
 * Коробка занятия в сетке по ключу строки.
 *
 * Перебором, а не селектором с подстановкой: ключ занятия приходит с сервера и
 * несёт двоеточия, а строка селектора это ещё и вопрос экранирования, который
 * незачем задавать ради трёх десятков элементов недели.
 */
function boxOfEntry(root: HTMLElement | null, key: string): HTMLElement | null {
  if (root === null) return null;
  for (const el of root.querySelectorAll<HTMLElement>(".cal__ev")) {
    if (el.dataset.entry === key) return el;
  }
  return null;
}
